import { useEffect, useState } from 'react';
import { AccountSearch, FilterDropdown } from '../../components';
import BillingHistoryTable from '../../components/billing-management/billingHistoryTable';
import { FilterOption } from '../../components/custom-input/FilterDropdown';
import { ClearFilterSVG, DownloadSVG, InfoSVG } from '../../components/svgs';
import CurrentDisabledAccountsTable from '../../components/billing-management/currentDisabledAccountsTable';
import MonthRangeInput from '../../components/custom-input/MonthInputRange';
import BillingAccountInfo from '../../components/billing-management/BillingAccountInfo';
import BillingEnableModal from '../../components/billing-management/BillingEnableModal';
import BillingDisableModal from '../../components/billing-management/BillingDisableModal';
import { ActionButton } from '../../components/action-buttons/ActionButtons';
import { showToast } from '../../services/toast.service';
import {
  toggleAccountBillingStatus,
  getAccountsOData,
  downloadAccountsReport,
  getAccountById,
  getBillingDisabledAccountsCount,
} from '../../api/accountApi';
import { downloadAccountsHistoryReport, getBillingHistoryOData } from '../../api/accountBillingExclusionHistory';
import { Account, StandardParamsToODataParams } from '../../types/interfaces';
import { AccountBillingExclusionHistory } from '../../types/interfaces/accountBillingExclusionHistory';
import { BillingStatus } from '../../types/enums/billingStatus.enum';
import {
  BUTTON_CONSTANTS,
  LABEL_CONSTANTS,
  ACCOUNT_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  VALIDATION_ALERT_CONSTANTS,
} from '../../constants/common';
import { formatDateToShortMonthDayYear, getPreviousMonthRange } from '../../utils/commonUtils';
import { formatDateForOData } from '../../utils/dateHandler';
import { toastMessages } from '../../constants/errorMessages';
import './billingManagement.scss';

const BillingManagement = () => {
  const [searchedAccount, setSearchedAccount] = useState<Account>();
  const [selectedAccount, setSelectedAccount] = useState<Account>();
  const [isAccountFromSearch, setIsAccountFromSearch] = useState<boolean>(false);
  const [billingDisabledAccountsCount, setBillingDisabledAccountsCount] = useState<number>();
  const [isOpenBillingEnableModal, setIsOpenBillingEnableModal] = useState<boolean>(false);
  const [isOpenBillingDisableModal, setIsOpenBillingDisableModal] = useState<boolean>(false);
  const [downloadingReport, setDownloadingReport] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<number>(0);
  const [historyRefresh, setHistoryRefresh] = useState<number>(0);
  const [billingDisabledAccountsData, setBillingDisabledAccountsData] = useState<Account[]>([]);
  const [status, setStatus] = useState<BillingStatus>(BillingStatus.ALL);
  const [monthRange, setMonthRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });
  const [historyDownloadingReport, setHistoryDownloadingReport] = useState<boolean>(true);
  const [historyFilterError, setHistoryFilterError] = useState<string>('');
  const [billingHistoryData, setBillingHistoryData] = useState<AccountBillingExclusionHistory[]>([]);
  const [historyMonthRange, setHistoryMonthRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>(getPreviousMonthRange);

  type ExportPayload = {
    'Report Type': string;
    'Created On': string;
    'Status Filter'?: string;
    'Report Period'?: string;
  };

  const statusOptions: FilterOption<BillingStatus>[] = [
    { name: 'All', value: BillingStatus.ALL },
    { name: 'Permanently Disabled', value: BillingStatus.PERMANENTLY_DISABLED },
    { name: 'Temporarily Disabled', value: BillingStatus.TEMPORARILY_DISABLED },
  ];

  const buildDisabledAccountsCountParams = () => {
    let isPermanentlyDisabled: boolean | undefined;

    if (status === BillingStatus.PERMANENTLY_DISABLED) isPermanentlyDisabled = true;
    else if (status === BillingStatus.TEMPORARILY_DISABLED) isPermanentlyDisabled = false;
    else isPermanentlyDisabled = undefined;

    return {
      fromMonth: monthRange.startDate ?? undefined,
      toMonth: monthRange.endDate ?? undefined,
      isPermanentlyDisabled: isPermanentlyDisabled,
    };
  };

  const buildBillingHistoryODataParams = () => {
    const { startDate, endDate } = historyMonthRange;

    if (!startDate || !endDate) return '';

    const formattedStart = formatDateForOData(startDate);
    const formattedEnd = formatDateForOData(endDate);

    return `BillingDisabledMonth ge ${formattedStart} and BillingDisabledMonth le ${formattedEnd}`;
  };

  const buildDisabledAccountsODataParams = () => {
    let filters: string[] = [];
    filters.push('BillingOffStartMonth ne null');

    const { startDate, endDate } = monthRange;

    if (startDate && endDate) {
      const additionalFilter: string[] = [];

      const formattedStartDate = formatDateForOData(startDate);
      const formattedEndDate = formatDateForOData(endDate);
      additionalFilter.push(
        `BillingOffStartMonth le ${formattedEndDate} and (BillingOffEndMonth ge ${formattedStartDate} or BillingOffEndMonth eq null)`,
      );
      filters.push(`(${additionalFilter.join(' and ')})`);
    }

    if (status === BillingStatus.PERMANENTLY_DISABLED) filters.push('BillingOffEndMonth eq null');
    else if (status === BillingStatus.TEMPORARILY_DISABLED) filters.push('BillingOffEndMonth ne null');

    return filters.join(' and ');
  };

  const buildExportQueryParams = () => {
    return {
      filter: buildDisabledAccountsODataParams(),
    };
  };

  const buildHistoryExportQueryParams = () => {
    return {
      filter: buildBillingHistoryODataParams(),
    };
  };

  const buildExportPayload = (): ExportPayload => {
    const payload: ExportPayload = {
      'Report Type': 'Billing Disabled Accounts',
      'Created On': new Date().toLocaleDateString(),
    };

    if (status === 'Permanently Disabled' || status === 'Temporarily Disabled') {
      payload['Status Filter'] = status;
    }

    if (monthRange.startDate && monthRange.endDate) {
      const startDate = formatDateToShortMonthDayYear(monthRange.startDate);
      const endDate = formatDateToShortMonthDayYear(monthRange.endDate);

      payload['Report Period'] = `${startDate} - ${endDate}`;
    }

    return payload;
  };

  const buildHistoryExportPayload = (): ExportPayload => {
    const payload: ExportPayload = {
      'Report Type': 'Billing Disabled Accounts History',
      'Created On': new Date().toLocaleDateString(),
    };

    if (historyMonthRange.startDate && historyMonthRange.endDate) {
      const startDate = formatDateToShortMonthDayYear(historyMonthRange.startDate);
      const endDate = formatDateToShortMonthDayYear(historyMonthRange.endDate);

      payload['Report Period'] = `${startDate} - ${endDate}`;
    }

    return payload;
  };

  const setReportName = (link: HTMLAnchorElement) => {
    const prefix = 'BillingDisabledAccountsReport';
    let fileName = '';

    if (monthRange.startDate && monthRange.endDate) {
      const startDate = formatDateToShortMonthDayYear(monthRange.startDate);
      const endDate = formatDateToShortMonthDayYear(monthRange.endDate);
      const reportPeriod = `${startDate} - ${endDate}`;
      fileName = `${prefix}_${reportPeriod}.xlsx`;
    } else {
      fileName = `${prefix}.xlsx`;
    }
    link.setAttribute('download', fileName);
  };

  const setHistoryReportName = (link: HTMLAnchorElement) => {
    const prefix = 'AccountBillingDisabledHistory';
    let fileName = '';

    if (historyMonthRange.startDate && historyMonthRange.endDate) {
      const startDate = formatDateToShortMonthDayYear(historyMonthRange.startDate);
      const endDate = formatDateToShortMonthDayYear(historyMonthRange.endDate);
      const reportPeriod = `${startDate} - ${endDate}`;
      fileName = `${prefix}_${reportPeriod}.xlsx`;
    } else {
      fileName = `${prefix}.xlsx`;
    }
    link.setAttribute('download', fileName);
  };

  const handleActionButtons = (account: Account) => {
    const enable: ActionButton = {
      name: `${BUTTON_CONSTANTS.ENABLE}`,
      action: () => handleAction(account, BUTTON_CONSTANTS.ENABLE),
    };
    const edit: ActionButton = { name: `${BUTTON_CONSTANTS.EDIT}`, action: () => handleAction(account, BUTTON_CONSTANTS.EDIT) };

    return [enable, edit];
  };

  const handleAction = (account: Account, action: string) => {
    if (account) {
      setSelectedAccount(account);
      action === BUTTON_CONSTANTS.ENABLE ? setIsOpenBillingEnableModal(true) : setIsOpenBillingDisableModal(true);
    }
  };

  const toggleEnableDisableButton = (billingOffStartMonth: Date | null | undefined) => {
    if (billingOffStartMonth !== null && billingOffStartMonth !== undefined) {
      setIsOpenBillingEnableModal(true);
    } else {
      setIsOpenBillingDisableModal(true);
    }
  };

  const fetchBillingUpdatedAccount = async (accountId: number) => {
    if (searchedAccount?.accountId === accountId) {
      try {
        const { data: updatedAccount } = await getAccountById(accountId);
        setSearchedAccount(updatedAccount);
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    }
    setRefresh(refresh => refresh + 1);
  };

  const handleAccountBilling =
    (action: typeof LABEL_CONSTANTS.ENABLE | typeof LABEL_CONSTANTS.DISABLE) =>
    async (data?: { startDate?: Date; endDate?: Date | null; notes?: string }) => {
      action === LABEL_CONSTANTS.ENABLE ? setIsOpenBillingEnableModal(false) : setIsOpenBillingDisableModal(false);

      if (selectedAccount?.accountId) {
        try {
          const payload = {
            billingOffStartMonth: action === LABEL_CONSTANTS.ENABLE ? null : data?.startDate || null,
            billingOffEndMonth: action === LABEL_CONSTANTS.ENABLE ? null : data?.endDate || null,
            notes: data?.notes || null,
          };
          await toggleAccountBillingStatus(+selectedAccount.accountId, payload);
          await fetchBillingUpdatedAccount(selectedAccount.accountId);
          showToast.info(
            action === LABEL_CONSTANTS.ENABLE
              ? toastMessages.ACCOUNT_BILLING_ENABLE + ' for ' + selectedAccount.name
              : toastMessages.ACCOUNT_BILLING_DISABLE + ' for ' + selectedAccount.name,
          );
        } catch (error: any) {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      }
    };

  const fetchBillingDisabledAccountDetails = async () => {
    try {
      const response = await getAccountsOData(StandardParamsToODataParams({ filter: buildDisabledAccountsODataParams() }));
      setBillingDisabledAccountsData(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const fetchBillingDisabledAccountsCount = async () => {
    try {
      const { fromMonth, toMonth, isPermanentlyDisabled } = buildDisabledAccountsCountParams();
      const response = await getBillingDisabledAccountsCount(fromMonth, toMonth, isPermanentlyDisabled);
      setBillingDisabledAccountsCount(response.data);
    } catch (error: any) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      setBillingDisabledAccountsCount(0);
    }
  };

  const fetchBillingHistory = async () => {
    try {
      const response = await getBillingHistoryOData(
        StandardParamsToODataParams({
          filter: buildBillingHistoryODataParams(),
          include: 'Account',
        }),
      );

      setBillingHistoryData(Array.isArray(response.data) ? response.data : []);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  useEffect(() => {
    fetchBillingDisabledAccountsCount();
    fetchBillingDisabledAccountDetails();
    setRefresh(prev => prev + 1);
  }, [status, monthRange.endDate]);

  useEffect(() => {
    fetchBillingDisabledAccountsCount();
    fetchBillingDisabledAccountDetails();
  }, [refresh]);

  useEffect(() => {
    fetchBillingHistory();
    setHistoryRefresh(prev => prev + 1);
  }, [historyMonthRange.startDate, historyMonthRange.endDate]);

  const handleResetFilters = () => {
    setStatus(BillingStatus.ALL);
    setMonthRange({ startDate: null, endDate: null });
  };

  const handleDownloadReport = async () => {
    if (!downloadingReport) return;

    try {
      setDownloadingReport(false);
      const queryParams = buildExportQueryParams();
      const payload = buildExportPayload();

      const response = await downloadAccountsReport(payload, queryParams);
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      setReportName(link);
      document.body.appendChild(link);
      link.click();
    } catch (error: any) {
      showToast.error(toastMessages.FAILED_TO_DOWNLOAD_REPORT);
    } finally {
      setDownloadingReport(true);
    }
  };

  const handleDownloadHistoryReport = async () => {
    if (!historyMonthRange.startDate || !historyMonthRange.endDate) {
      setHistoryFilterError(VALIDATION_ALERT_CONSTANTS.HISTORY_DOWNLOAD_ALERT);
      return;
    }

    if (!historyDownloadingReport) return;

    try {
      setHistoryDownloadingReport(false);
      setHistoryFilterError('');
      const queryParams = buildHistoryExportQueryParams();
      const payload = buildHistoryExportPayload();

      const response = await downloadAccountsHistoryReport(payload, queryParams);
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      setHistoryReportName(link);
      document.body.appendChild(link);
      link.click();
    } catch (error: any) {
      showToast.error(toastMessages.FAILED_TO_DOWNLOAD_REPORT);
    } finally {
      setHistoryDownloadingReport(true);
    }
  };

  useEffect(() => {
    if (historyFilterError) {
      setHistoryFilterError('');
    }
  }, [historyMonthRange.startDate, historyMonthRange.endDate]);

  const getLastCompletedMonth = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 0);
  };

  const lastCompletedMonth = getLastCompletedMonth();

  return (
    <>
      <div>
        <div className="info-banner">
          <InfoSVG size="18" color="#0f64dcf5" />
          {ACCOUNT_CONSTANTS.BILLING_FINALIZATION_NOTE}
        </div>
        <h3 className="tab-header">{PAGE_HEADER_CONSTANTS.ADMIN_BILLING_MANAGEMENT}</h3>
      </div>
      <div className="search-row">
        <label>{ACCOUNT_CONSTANTS.SEARCH_ACCOUNT}:</label>
        <div className="input-container">
          <AccountSearch
            onChange={(account: Account | undefined) => {
              setSearchedAccount(account);
              setIsAccountFromSearch(true);
            }}
          />
        </div>
      </div>
      {searchedAccount?.accountId && isAccountFromSearch && (
        <>
          <div className="account-info-text">{ACCOUNT_CONSTANTS.ACCOUNT_INFO}:</div>
          <div className="billing-account-information">
            <BillingAccountInfo account={searchedAccount} />
          </div>
          <div className="billing-container">
            <label>{ACCOUNT_CONSTANTS.BILLING_ENABLE_DISABLE_MESSAGE}</label>
            <button
              className="small green button"
              onClick={() => {
                setSelectedAccount(searchedAccount);
                toggleEnableDisableButton(searchedAccount.billingOffStartMonth);
              }}>
              {searchedAccount.billingOffStartMonth === null ? BUTTON_CONSTANTS.DISABLE : BUTTON_CONSTANTS.ENABLE}
            </button>
          </div>
        </>
      )}
      <hr className="divider" />

      <div className="filter-by">
        <div className="label">
          <div className="reset-filter-row">
            <p>{LABEL_CONSTANTS.FILTER_BY}:</p>
            <button type="button" className="reset-filters-button" onClick={handleResetFilters}>
              <ClearFilterSVG /> Reset Filters
            </button>
          </div>
        </div>
        <div className="button-row">
          <div className="filters">
            <div className="filter-category">
              <FilterDropdown
                placeholder="Status"
                options={statusOptions}
                value={{ name: status, value: status }}
                onChange={e => setStatus(e.value)}
              />
            </div>
            <div className="filter-category">
              <MonthRangeInput value={monthRange} onChange={setMonthRange} minDate={new Date()} maxDate={null} isClearable />
            </div>
          </div>
          <div className="download-button-row">
            <button
              className="button outlined download-report"
              onClick={handleDownloadReport}
              disabled={billingDisabledAccountsCount === 0}>
              <DownloadSVG />
              &nbsp;{LABEL_CONSTANTS.DOWNLOAD}
            </button>
          </div>
        </div>
      </div>

      <div className="disabled-accounts-count">
        <label>
          {ACCOUNT_CONSTANTS.BILLING_DISABLED_ACCOUNTS_COUNT} {billingDisabledAccountsCount}
        </label>
      </div>

      <CurrentDisabledAccountsTable
        refresh={refresh}
        buildFilter={buildDisabledAccountsODataParams}
        handleActionButtons={handleActionButtons}
        billingDisabledAccountsData={billingDisabledAccountsData}
      />

      <hr className="divider" />
      <div className="billing-history-wrapper">
        <h4 className="section-title">{LABEL_CONSTANTS.DISABLED_ACCOUNTS_BILLING_HISTORY}:</h4>
        <div className="filter-by">
          <div className="label">
            <div className="reset-filter-row">
              <p>{LABEL_CONSTANTS.FILTER_BY}:</p>
              <button
                type="button"
                className="reset-filters-button"
                onClick={() => {
                  setHistoryMonthRange({ startDate: null, endDate: null });
                  setHistoryFilterError('');
                }}>
                <ClearFilterSVG /> {LABEL_CONSTANTS.RESET_FILTERS}
              </button>
            </div>
          </div>

          <div className="button-row">
            <div className="filters">
              <div className="filter-category">
                <MonthRangeInput
                  value={historyMonthRange}
                  onChange={setHistoryMonthRange}
                  minDate={null}
                  maxDate={lastCompletedMonth}
                  isClearable
                />
              </div>
            </div>

            <div className="download-button-row">
              <button
                className="button outlined download-report"
                onClick={handleDownloadHistoryReport}
                disabled={billingHistoryData.length === 0}>
                <DownloadSVG />
                &nbsp;{LABEL_CONSTANTS.DOWNLOAD}
              </button>
            </div>
          </div>
        </div>

        {historyFilterError && <div className="filter-error-message">{historyFilterError}</div>}

        <BillingHistoryTable refresh={historyRefresh} buildFilter={buildBillingHistoryODataParams} data={billingHistoryData} />
      </div>

      {isOpenBillingEnableModal && (
        <BillingEnableModal
          isOpen={isOpenBillingEnableModal}
          account={selectedAccount}
          onClose={() => setIsOpenBillingEnableModal(false)}
          onConfirm={handleAccountBilling(LABEL_CONSTANTS.ENABLE)}
        />
      )}

      {isOpenBillingDisableModal && (
        <BillingDisableModal
          isOpen={isOpenBillingDisableModal}
          account={selectedAccount}
          onClose={() => setIsOpenBillingDisableModal(false)}
          onConfirm={handleAccountBilling(LABEL_CONSTANTS.DISABLE)}
        />
      )}
    </>
  );
};

export default BillingManagement;
